<script lang="ts" setup>
  import { onMounted, onUnmounted, computed, watch, ref, onBeforeUnmount, useId } from 'vue';
  import { useViewParentLocation, useHttpClient, useDialogStack } from '@othercommerce/foundation';

  interface Props {
    id?: string;
    visitable?: boolean;
    small?: boolean;
    medium?: boolean;
    large?: boolean;
    larger?: boolean;
    mobileCenter?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    visitable: false,
    small: false,
    medium: false,
    large: false,
    larger: false,
    mobileCenter: false,
  });

  const open = defineModel('open', { type: Boolean, required: false, default: false });

  const randomId = useId();
  const http = useHttpClient();
  const parent = useViewParentLocation();
  const stack = useDialogStack();

  const htmlModalElement = ref();

  const id = computed(() => {
    return props.id || randomId;
  });

  const modalClasses = computed(() => {
    return {
      'modal--center': props.mobileCenter,
    };
  });

  const wrapperClasses = computed(() => {
    return {
      'wrapper--small': props.small,
      'wrapper--medium': props.medium,
      'wrapper--large': props.large,
      'wrapper--larger': props.larger,
    };
  });

  function show() {
    htmlModalElement.value.showModal();
    stack.push(id.value);
  }

  function hide() {
    htmlModalElement.value.close();
    stack.pop(id.value);
  }

  function onCancel(event: Event) {
    if (props.visitable) {
      event.preventDefault();
      event.stopPropagation();

      if (parent.value) {
        http.get(parent.value);
      }
    }
  }

  function onPopstate() {
    hide();
  }

  onMounted(() => {
    if (open.value) {
      show();
    }
  });

  onBeforeUnmount(async () => {
    hide();
  });

  onMounted(() => {
    if (!props.visitable) {
      window.addEventListener('popstate', onPopstate);
    }
  });

  onUnmounted(() => {
    if (!props.visitable) {
      window.removeEventListener('popstate', onPopstate);
    }
  });

  watch(open, (value) => {
    if (value) {
      show();
    } else {
      hide();
    }
  });
</script>
<template>
  <dialog ref="htmlModalElement" :id="id" class="modal" :class="modalClasses" v-bind="$attrs" @close.stop="open = false" @keydown.stop.esc="onCancel" @change.stop="void 0">
    <div class="modal-container">
      <div class="wrapper" :class="wrapperClasses">
        <div class="content">
          <slot />
        </div>
      </div>
    </div>
  </dialog>
</template>
<style>
  :root {
    --ui-modal-wrapper-border-radius: calc(var(--section-border-radius) + 0.375rem);
    --ui-modal-content-border-radius: var(--section-border-radius);
    --ui-modal-backdrop: var(--backdrop-default);
    --ui-modal-wrapper-background: rgb(255 255 255 / 0.6);
    --ui-modal-wrapper-border-color: rgba(2, 28, 55, 0.08);
  }
</style>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .modal {
    background: none;
    border: none;
    min-width: 100%;
    min-height: 100dvh;
    cursor: default;
    padding: 0;
    margin: 0;
    top: 0;
    left: 0;

    .modal-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 100dvh;
      padding: 2rem;

      .wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 100%;
        max-width: 28rem;
        min-height: auto;
        padding: 0.375rem;
        background: var(--ui-modal-wrapper-background);
        border: 1px solid var(--ui-modal-wrapper-border-color);
        backdrop-filter: blur(2px);
        border-radius: var(--ui-modal-wrapper-border-radius);

        &--small {
          max-width: 32rem;
        }

        &--medium {
          max-width: 42rem;
        }

        &--large {
          max-width: 64rem;
        }

        &--larger {
          max-width: 80rem;
        }

        .content {
          display: flex;
          flex-direction: column;
          background: #fff;
          box-shadow: 0 0 2px 0 rgb(0 0 0 / 0.25), 0 25px 50px -12px rgb(0 0 0 / 0.32);
          border-radius: var(--ui-modal-content-border-radius);
          padding: 2rem;
          width: 100%;
          gap: 2rem;
        }
      }
    }
  }

  @include media.query(md) {
    .modal {
      .modal-container {
        align-items: flex-end;
        padding: 0;

        .wrapper {
          max-height: 90svh;
          padding: 0;
          border: none;
          backdrop-filter: none;
          max-width: unset;

          .content {
            padding: 1rem 1rem 1.75rem;
            box-shadow: none;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            gap: 1.5rem;
          }
        }
      }

      &--center {
        .modal-container {
          align-items: center;
          padding: 1rem;

          .wrapper {
            padding: 0.375rem;

            .content {
              border-radius: var(--border-radius);
              gap: 1rem;
            }
          }
        }
      }
    }
  }

  dialog[open] {
    opacity: 1;
    transform: scale(1) translateY(0);
  }

  dialog {
    color: var(--color-default, #000);
    opacity: 0;
    transform: scale(0.9);
    transition-property: opacity, transform, overlay, display;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
    transition-behavior: allow-discrete;
  }

  @starting-style {
    dialog[open] {
      opacity: 0;
      transform: scale(0.9);
    }
  }

  dialog::backdrop {
    background-color: transparent;
    transition-property: background-color, overlay, display;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
    transition-behavior: allow-discrete;
  }

  dialog[open]::backdrop {
    background-color: var(--ui-modal-backdrop);
  }

  @starting-style {
    dialog[open]::backdrop {
      background-color: transparent;
    }
  }

  @include media.query(md) {
    dialog[open]:not(.modal--center) {
      transform: translateY(0);
    }

    dialog:not(.modal--center) {
      transform: translateY(50%);
    }

    @starting-style {
      dialog[open]:not(.modal--center) {
        transform: translateY(50%);
      }
    }
  }
</style>
